/**
 * usePlanPreview · 文件夹方案预览容器逻辑 (live + history 统一)
 *
 * v6 重构合并: 取代之前的 useLivePlanPreview (selection 单例 + anchor 单例挂载)
 * 和 useHistoryPlanPreview (message 私有), 让 live / history 走完全相同的数据
 * 流 — 每条 folder_plan card 自己持有方案快照 + 各自渲染。
 *
 * 数据来源 (二层):
 *   · 浅字段 (sessionId / chainVersion / layersSummary / expandedSize /
 *     layerCount) · 写入时即知, 写入路径:
 *       - live: SSE dimension_updated → assistantPlanSnapshotted
 *       - history: aiChatHistory.buildFolderContent (浅字段部分由 history 接
 *         口 5 hydrate 填充, dimension 摘要只 live 有)
 *   · 深字段 (folders / excludedKeys / taskCreated) · hydrate 后才有:
 *       - live: SSE done 后 buildStreamHandlers 用 backendMessageId 调接口 5
 *       - history: conversationRestore.hydrateFolderMessages 并发拉接口 5
 *
 * mode 判定 (跟旧 history hook 同源):
 *   · confirmed（taskCreated=true 或后续出现建夹任务卡）→ readonly + 标"已确认"
 *   · superseded（后续出现新 folder_plan，被 refine 取代）→ readonly 仅置灰
 *   · 无 sessionId / taskCreated 未确定（老数据/数据残缺） → readonly 仅置灰
 *
 * 不依赖 selection 单例 — 多条 folder_plan 卡可同时存在, 每条按自己的 card
 * 状态独立渲染。
 */

import { useCallback, useEffect, useMemo, useState } from 'react'
import {
  useAppDispatch,
  useAppSelector,
  useVipKind
} from '../../../../app/hooks'
import { selectFolderPlanConsumedState } from '../../../../slices/aiClassify'
import {
  fetchFolderQuota,
  selectFolderQuota
} from '../../../../slices/folderQuotaSlice'
import { derivePaywallView } from '../../../components/FolderPaywall/paywall'
import type { FolderPlanCard } from '../../../../types/ai-classify'
import type {
  ActionBarConfig,
  FolderPlanPreviewMode,
  FolderPlanPreviewViewProps
} from './types'
import {
  collectAllNodeIds,
  countAllNodes,
  countIncludedNodes,
  planFoldersToPreviewNodes
} from './planPreviewUtils'
import { useConfirmCreate } from './useConfirmCreate'

/**
 * 客户拜访配置 · 团队可自定义「名词 + 动词」(如 门店 / 巡店)，
 * 由 App 启动时 fetchGroupCustomerConfig 拉到 group store(默认空对象)。
 */
interface CustomerVisitConfig {
  noun?: string
  verb?: string
}

export interface UsePlanPreviewResult {
  viewProps: FolderPlanPreviewViewProps
}

export function usePlanPreview(
  card: FolderPlanCard,
  messageId: string
): UsePlanPreviewResult {
  const dispatch = useAppDispatch()
  const groupID = useAppSelector((s) => s.group.groupId)

  // 客户拜访维度展示名走团队自定义 ${noun}${verb}(如「门店巡店」)，不写死「客户拜访」。
  // 配置缺失(未拉到 / 老团队无配置)时回落 undefined，由 View 兜底成 DIMENSION_LABEL。
  const customerConfig = useAppSelector(
    (s) => s.group.customerConfig as CustomerVisitConfig | undefined
  )
  const customerVisitLabel =
    customerConfig && customerConfig.noun && customerConfig.verb
      ? `${customerConfig.noun}${customerConfig.verb}`
      : undefined

  const folders = card.folders ?? []
  // "被消费"派生 · 弥补 card.taskCreated 在 文本 confirm / refine 取代 两条
  // 路径下不会被回写的盲区，并区分成因（决定锁死后的标记文案）:
  //   · confirmed  · 之后先出现 build_task_status → 已建夹（任务必属本卡）
  //   · superseded · 之后先出现新 folder_plan → 被新一轮 refine 取代，从未确认
  // 详见 selectFolderPlanConsumedState 注释
  const consumedState = useAppSelector(selectFolderPlanConsumedState(messageId))
  /** 真·已确认创建（接口5 回写 或 文本路径确认） */
  const confirmed = card.taskCreated === true || consumedState === 'confirmed'
  const sessionId = card.sessionId || ''
  const canEdit = !confirmed && consumedState !== 'superseded' && !!sessionId

  const mode: FolderPlanPreviewMode = canEdit
    ? 'history_editable'
    : 'history_readonly'

  /**
   * 只读成因 · View 据此决定标题旁的副标记：
   *  · confirmed → "已确认"
   *  · superseded（被新方案取代）/ unknown（老数据缺 sessionId）→ 不标记仅置灰
   */
  const readonlyReason = canEdit
    ? null
    : confirmed
      ? ('confirmed' as const)
      : consumedState === 'superseded'
        ? ('superseded' as const)
        : ('unknown' as const)

  const roots = useMemo(() => planFoldersToPreviewNodes(folders), [folders])

  // 接口 5 的 excluded_folder_keys 即后端 chainKey, 跟 node.excludeKey 同源
  const readonlyExcludedKeys = useMemo(
    () => new Set(Array.isArray(card.excludedKeys) ? card.excludedKeys : []),
    [card.excludedKeys]
  )

  // editable 模式下的本地反选状态 · 初始空集合, 跟 readonly 互斥
  // (editable 下用户尚未 confirm 过, 不会有"已落库"的反选要还原)
  const [localExcludedKeys, setLocalExcludedKeys] = useState<Set<string>>(
    () => new Set()
  )
  const excludedKeySet = canEdit ? localExcludedKeys : readonlyExcludedKeys

  const vipKind = useVipKind()
  const quota = useAppSelector(selectFolderQuota)

  // 卡点需要 used/cap：进可编辑态确保 quota 已加载（含非会员；配额 widget 对非会员不拉）
  useEffect(() => {
    if (canEdit && groupID && !quota.loaded && !quota.loading) {
      dispatch(fetchFolderQuota({ groupID: String(groupID) }))
    }
  }, [canEdit, groupID, quota.loaded, quota.loading, dispatch])

  const totalCount = useMemo(() => countAllNodes(folders), [folders])
  const selectedCount = useMemo(
    () => countIncludedNodes(folders, excludedKeySet),
    [folders, excludedKeySet]
  )
  const paywall = useMemo(
    () =>
      derivePaywallView({
        vipKind,
        used: quota.used,
        cap: quota.cap,
        selectedCount,
        tiers: quota.tiers
      }),
    [vipKind, quota.used, quota.cap, selectedCount, quota.tiers]
  )

  const initialExpanded = useMemo(() => collectAllNodeIds(folders), [folders])
  const [expandedKeys, setExpandedKeys] = useState<Set<string>>(initialExpanded)

  const onToggleExpand = useCallback((key: string) => {
    setExpandedKeys((prev) => {
      const next = new Set(prev)
      if (next.has(key)) next.delete(key)
      else next.add(key)
      return next
    })
  }, [])

  const onToggleExclude = useCallback(
    (excludeKey: string) => {
      if (!canEdit) return
      setLocalExcludedKeys((prev) => {
        const next = new Set(prev)
        if (next.has(excludeKey)) next.delete(excludeKey)
        else next.add(excludeKey)
        return next
      })
    },
    [canEdit]
  )

  const allSelected = totalCount > 0 && selectedCount === totalCount

  /**
   * 全选/取消全选 · 全选=清空反选集合；取消全选=排除全部顶层
   * （子级随祖先级联整树排除，与单行勾选的级联语义一致，
   *  之后重新勾选某个顶层即恢复其整棵子树）
   */
  const onToggleSelectAll = useCallback(() => {
    if (!canEdit) return
    if (allSelected) {
      setLocalExcludedKeys(
        () =>
          new Set(
            folders.map((f) => f.chainKey).filter((key): key is string => !!key)
          )
      )
    } else {
      setLocalExcludedKeys(() => new Set())
    }
  }, [canEdit, allSelected, folders])

  const {
    confirm: onConfirm,
    confirming,
    error
  } = useConfirmCreate({
    groupID: String(groupID || ''),
    sessionId,
    messageId,
    folders,
    localExcludedKeys,
    paywall,
    canEdit,
    selectedCount,
    vipKind,
    used: quota.used,
    cap: quota.cap,
    tiers: quota.tiers
  })

  // expectedTotal 优先用 card.expandedSize (live snapshot 写入), fallback 数
  // folders 节点 (history 路径 / live hydrate 后兜底)
  const expectedTotal = useMemo(() => {
    if (typeof card.expandedSize === 'number') return card.expandedSize
    return countAllNodes(folders)
  }, [card.expandedSize, folders])

  // 底部操作条 · 可编辑→计数 + 抽样/确认按钮；只读且有方案→仅计数；只读且无方案→不渲染
  const actionBar = useMemo<ActionBarConfig | null>(() => {
    if (!canEdit && totalCount === 0) return null
    return {
      editable: canEdit,
      selectedCount,
      totalCount,
      confirming,
      hasFolders: roots.length > 0,
      confirmText: paywall.confirmText,
      onConfirm
    }
  }, [
    canEdit,
    selectedCount,
    totalCount,
    confirming,
    roots.length,
    paywall.confirmText,
    onConfirm
  ])

  const viewProps: FolderPlanPreviewViewProps = {
    mode,
    readonlyReason,
    layersSummary: card.layersSummary ?? [],
    customerVisitLabel,
    expectedTotal,
    roots,
    expandedKeys,
    loadingKeys: new Set(),
    excludedKeys: excludedKeySet,
    // folders 没 hydrate 前显示 loading 占位 (live snapshot 后 SSE done 前的窗口)
    loading: card.folders === undefined,
    error,
    actionBar,
    // 只读态（已建夹/历史）不出现卡点 banner —— 与按钮仅 editable 渲染对齐（spec §6）
    banner: canEdit ? paywall.banner : null,
    // 全选/取消全选行 · 仅可编辑态渲染（只读没有勾选语义）
    selectAll: canEdit
      ? {
          checked: allSelected,
          indeterminate: selectedCount > 0 && selectedCount < totalCount,
          onToggle: onToggleSelectAll
        }
      : null,
    onToggleExpand,
    onToggleExclude
  }

  return { viewProps }
}
